import { useEffect, useState } from "react";
import WacoHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Waco?", a: "We offer Waco-area clients expedited processing as fast as 24 hours. Standard expedited service typically takes 5–10 business days. Waco is located between Dallas and Austin, giving residents access to multiple passport agency options, but our courier service eliminates the need for any travel. The exact timeline depends on your application type and document completeness." },
  { q: "Where is the nearest passport acceptance facility in Waco?", a: "Waco has several acceptance facilities including the Waco Main Post Office, Hewitt Post Office, and the McLennan County Clerk. The nearest passport agency is in Houston (185 miles) or Dallas (90 miles). For time-critical cases, our courier service means you never need to make that drive." },
  { q: "Can Baylor students get expedited passports for study abroad?", a: "Absolutely. Baylor University sends hundreds of students abroad every semester. The deadline pressure is intense — students who discover a passport issue 3 weeks before departure can lose their entire program. We process Baylor student passports with priority review and document checking." },
  { q: "How much does expedited passport service cost for Waco residents?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee covering document review, overnight shipping, and expedited submission. Our rates are typically 30–100% lower than FedEx, Staples, and other resellers. We offer a best price guarantee." },
  { q: "Do I need to visit a passport office in Waco?", a: "No. Our service is remote. You complete forms online, visit a local acceptance facility (like the Waco post office or county clerk) for in-person verification, then ship your documents to us via overnight courier. We handle the expedited submission and return your passport to your Waco address." },
  { q: "Can I get a passport photo in Waco?", a: "Yes. Many CVS, Walgreens, and post office locations in Waco and Hewitt offer passport photos. The Waco Post Office may include photos with their acceptance facility. We recommend confirming photo availability before visiting. Passport photos must meet strict State Department specifications." },
  { q: "What if my Waco church group is going on a mission trip?", a: "Waco churches — from First Baptist to Highland Church — send mission groups internationally. Group trips require every participant to have a valid passport. When a group member discovers their passport expired, the entire trip is at risk. We process group mission trip passport applications with the same urgency." },
  { q: "Can I track my Waco passport application?", a: "Yes. Once submitted, we provide tracking at every stage — from Waco to our processing center, through State Department review, and return shipping. You also receive the State Department's online tracking information." },
  { q: "What if my passport is lost or stolen in Waco?", a: "File a police report if stolen, complete Form DS-64 along with DS-11 for a replacement. Visit a local acceptance facility in person. For upcoming travel, our expedited service can process the replacement in as fast as 24 hours." },
  { q: "Does Waco have a walk-in passport agency?", a: "No, Waco does not have a passport agency. The nearest are in Dallas and Houston. However, our registered courier service handles the same expedited submission process without requiring you to travel. You ship your application to us; we handle the agency submission." },
];

function WacoStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) { setActive(id); break; }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" }, { id: "services", label: "Services" }, { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" }, { id: "destinations", label: "Destinations" }, { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" }, { id: "comparison", label: "Compare" }, { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" }, { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>{l.label}</a>
        ))}
      </div>
    </div>
  );
}

function WacoProcessingSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Waco Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Multiple speed tiers for Waco residents — Baylor students, mission groups, and Central Texas families.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Waco residents with imminent departure within 3 days. Same-day document review and overnight courier submission." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For Baylor students and business travelers with travel in 1–2 weeks. Full document review included." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Waco residents planning ahead. Faster than standard post office processing with professional handling." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WacoServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Waco travelers with imminent departure from Central Texas." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Waco residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time applications for Baylor students and Waco adults, with full document review." },
    { icon: "ri-graduation-cap-line", title: "Study Abroad", desc: "Expedited service for Baylor and McLennan Community College students with program deadlines." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Hewitt, Woodway, and Bellmead families." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for Waco residents." },
    { icon: "ri-landscape-line", title: "Mission Trip", desc: "Expedited processing for Waco church and mission groups traveling internationally." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors before Waco applications reach the State Department." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Waco Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Comprehensive passport solutions for Waco's Baylor community, mission groups, and Central Texas families.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WacoLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Waco</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Waco sits at the heart of Central Texas, straddling the Brazos River between Dallas and Austin. The city of 140,000 people is home to Baylor University — the largest Baptist university in the world — with nearly 20,000 students and a steady stream of international travel demand. From Baylor's study abroad programs to research conferences, from mission trips to global business partnerships, the university generates a constant flow of passport demand that is unique to a city of Waco's size.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The Magnolia Market at the Silos and the broader Fixer Upper tourism phenomenon has transformed Waco into a national travel destination. But the international dimension is equally significant: Waco's growing hospitality and tourism industry now attracts international visitors, and local businesses and hoteliers travel abroad for training, conferences, and partnership development. The International Association of Convention and Visitors Bureaus, the Asia-Pacific hospitality sector, and European tourism markets are all part of Waco's evolving travel landscape.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Waco's mission culture is a defining characteristic of the city. Dozens of churches — from Highland Church to First Baptist Waco to Central Church — send mission teams to Central America, East Africa, Southeast Asia, and South America. These mission trips are typically scheduled months in advance, but last-minute team member changes — a new volunteer replacing a drop-out, or a passport discovered to be expired — create the exact compressed timeline that expedited passport service addresses.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The McLennan County manufacturing base, including the Waco Industrial Park, employs thousands of workers across aerospace, defense, and electronics manufacturing. These companies regularly send engineers and technicians to international trade shows, client installations, and quality audits in Mexico, China, Europe, and Southeast Asia. Waco-based manufacturing is not an obvious passport market, but it represents a steady, growing demand source.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            Waco's unique geographic position — 90 miles from Dallas and 100 miles from Austin — means residents can access two major international airports. But it also means Waco has no passport agency of its own. The closest is in Houston, nearly 185 miles south. Our remote courier service removes that geographic disadvantage entirely.
          </p>
        </div>
      </div>
    </section>
  );
}

function WacoTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Waco Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {[
            { icon: "ri-graduation-cap-line", title: "Baylor University Study Abroad", sub: "20,000+ students, dozens of international programs", body: "Baylor sends students to London, Spain, Australia, Italy, Greece, and many other destinations through semester programs and Maymester courses. Program departure dates are fixed — the academic calendar is not negotiable. Students who discover a passport issue in the final weeks before a semester abroad are in a time-critical situation. We process Baylor student applications with priority, and our document review is especially thorough for first-time applicants.", tip: "Baylor tip: Many study abroad programs require 6+ months of validity beyond the return date. Check your passport expiration date as soon as you apply for a program, not after you're accepted." },
            { icon: "ri-flight-takeoff-line", title: "Dallas-Fort Worth & Austin Airports", sub: "Waco's two major international gateways", body: "Waco Regional Airport (ACT) has limited direct service. Most international travelers from Waco drive to Dallas-Fort Worth International Airport (DFW) or Austin-Bergstrom International Airport (AUS), each about 90–100 miles. Both airports offer extensive international connections. The key point for Waco travelers: verify your passport validity before you book the flight — not the night before you leave for the airport.", tip: "Airport tip: Waco to DFW is about 1.5 hours; Waco to AUS is about 1 hour 45 minutes. Budget this time into your travel planning, and budget passport processing time into your calendar." },
            { icon: "ri-heart-pulse-line", title: "Mission Trips & Church Travel", sub: "Waco's strong faith community sends teams globally", body: "Waco churches send mission teams to Honduras, Guatemala, Kenya, Uganda, Thailand, and the Philippines. These trips are typically scheduled 6–12 months in advance, but team member changes happen late — a volunteer drops out and a new one steps in with no passport. Or an existing team member discovers their passport expired in the final month. Our expedited service is specifically designed for these scenarios.", tip: "Mission tip: Many developing countries require 6 months of passport validity plus 2 blank pages. Check your passport 3 months before departure, not 3 weeks." },
            { icon: "ri-tools-line", title: "Waco Manufacturing & Business Travel", sub: "Aerospace, defense, and electronics exports", body: "Waco's McLennan County manufacturing base includes companies that travel to Mexico, China, Europe, and Southeast Asia for client installations, trade shows, and quality audits. Business travel typically has 2–4 weeks of notice, and international travel often involves visa requirements that add passport validity pressure. We process Waco business travel passport requests with the same urgency.", tip: "Business tip: Many Asian countries require 6 months of validity plus visa processing time. A Chinese or Indian visa can take 2 weeks — so your passport needs to be valid for longer than you think." },
          ].map((card, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                  <i className={`${card.icon} text-[#1A365D] text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{card.title}</h3>
                  <p className="text-gray-400 text-xs">{card.sub}</p>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">{card.body}</p>
              <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
                <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Tip:</strong> {card.tip}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-store-2-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Waco Tourism &amp; International Business</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              The Magnolia Market phenomenon has made Waco an international tourism destination. Local tourism businesses and hospitality staff now travel abroad for training, trade shows, and partnership building. We process Waco tourism business passport applications with the same professional attention.
            </p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">Get Started <i className="ri-arrow-right-line ml-1"></i></a>
        </div>
      </div>
    </section>
  );
}

function WacoDestinationsSection() {
  const destinations = [
    { name: "United Kingdom", context: "Baylor study abroad in London, UK university partnerships, Waco business travel to Europe", urgency: "Baylor semester programs have fixed start dates — no late arrivals", icon: "ri-map-pin-2-line" },
    { name: "Spain & Italy", context: "Baylor study abroad programs, mission trips, cultural heritage travel for Waco families", urgency: "Academic and mission trip dates are immovable", icon: "ri-map-pin-2-line" },
    { name: "Central America", context: "Honduras, Guatemala, and Costa Rica mission trips, church service travel, agricultural partnerships", urgency: "Mission trips are scheduled around church calendars with no flexibility", icon: "ri-map-pin-2-line" },
    { name: "Mexico", context: "Waco business travel, family visits, medical tourism, and cross-border partnerships", urgency: "Business meetings and family events often have fixed dates", icon: "ri-map-pin-2-line" },
    { name: "East Africa", context: "Waco church mission trips to Kenya, Uganda, and Tanzania, NGO partnerships", urgency: "Church calendars and mission board commitments are locked months in advance", icon: "ri-map-pin-2-line" },
    { name: "Asia-Pacific", context: "Waco manufacturing and aerospace business travel, tourism industry partnerships", urgency: "Trade shows and business meetings have fixed dates that cannot be rescheduled", icon: "ri-map-pin-2-line" },
  ];
  const stats = [
    { stat: "20,000", label: "Baylor students with study abroad creating peak demand during academic deadlines" },
    { stat: "90–100 Miles", label: "Distance from Waco to DFW and Austin airports — the gateway to international travel" },
    { stat: "185 Miles", label: "Distance from Waco to the nearest passport agency in Houston" },
    { stat: "24 Hours", label: "Fastest turnaround for emergency Waco passport requests" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Waco Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/dallas" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Dallas <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/austin" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Austin <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/houston" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Houston <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WacoTestimonialsSection() {
  const testimonials = [
    { name: "Sarah T.", location: "Baylor University", text: "My study abroad semester in London was 3 weeks away and I realized my passport was expired. Fast Passport Center had it back to me in 6 days. I made my flight with time to spare.", rating: 5 },
    { name: "Pastor James R.", location: "Waco Church", text: "We had a mission team heading to Honduras and one of our volunteers had never applied for a passport. We called on Monday — they had their passport by Friday. The trip was saved.", rating: 5 },
    { name: "Michael L.", location: "Hewitt", text: "Business meeting in China in 10 days. My passport only had 4 months left. They walked me through the renewal process and got it back in 4 days. Professional and responsive.", rating: 5 },
    { name: "The Garcia Family", location: "Woodway", text: "First-time passports for our kids ahead of a family trip to Cancun. They made it simple, reviewed our documents before we went to the post office, and everything was approved first time.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Waco Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">{Array.from({ length: t.rating }).map((_, j) => (<i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>))}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div><p className="font-semibold text-[#1A365D] text-sm">{t.name}</p><p className="text-gray-400 text-xs">{t.location}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WacoFacilitiesSection() {
  const facilities = [
    { name: "Waco Main Post Office", address: "2121 W Waco Dr, Waco, TX 76710", phone: "(254) 754-0311", photo: "Yes", appointment: "Required" },
    { name: "Hewitt Post Office", address: "116 Winder St, Hewitt, TX 76643", phone: "(254) 666-6663", photo: "Yes", appointment: "Walk-in" },
    { name: "Bellmead Post Office", address: "3091 Bellmead Dr, Waco, TX 76705", phone: "(254) 799-1511", photo: "Yes", appointment: "Walk-in" },
    { name: "McLennan County Clerk", address: "200 W Waco Dr, Waco, TX 76701", phone: "(254) 757-5078", photo: "No", appointment: "Walk-in" },
    { name: "Woodway Post Office", address: "6700 Woodway Dr, Waco, TX 76712", phone: "(254) 772-5777", photo: "Yes", appointment: "Walk-in" },
    { name: "Temple Post Office", address: "2 N 3rd St, Temple, TX 76501", phone: "(254) 778-3781", photo: "Yes", appointment: "Required" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Waco Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility in person. Here are Waco-area options.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. We recommend calling ahead to confirm availability.</p>
        </div>
      </div>
    </section>
  );
}

function WacoComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Waco Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Third-Party Resellers</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "7–14 days" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "Minimal or extra fee" },
                { feature: "Student Experience", us: "Yes, Baylor programs", post: "Generic", reseller: "Generic" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Often 30–100% markup" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Reseller, not registered" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "Often limited" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", reseller: "Varies by plan" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "Varies" },
                { feature: "Same-Day Submission", us: "Yes, for emergency tier", post: "No", reseller: "Rarely" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function WacoMistakesSection() {
  const mistakes = [
    { title: "Waiting for Baylor Program Deadlines", desc: "Baylor study abroad students often wait until the program application deadline to check their passport. The program deadline is not the passport deadline — you need to apply for the passport first, then apply for the program." },
    { title: "Assuming the Closest Post Office is the Fastest", desc: "Waco has multiple acceptance facilities with different appointment availability. The Waco Main Post Office is busiest. Hewitt and Bellmead post offices may have shorter wait times." },
    { title: "Using the Wrong Form", desc: "DS-11 for new/child passports, DS-82 for renewals. Using the wrong form causes rejection at the acceptance facility. First-time applicants at Baylor often use the wrong form because the instructions are confusing." },
    { title: "Mission Trip Timelines Too Tight", desc: "Waco church groups often set a passport deadline that is too optimistic — 4 weeks before departure when standard processing takes 10–13 weeks. Build in at least 8 weeks for first-time applicants, or use our expedited service." },
    { title: "Not Checking Visa Requirements", desc: "Some of the most common Waco travel destinations — China, India, Kenya — require visas that can take 2 weeks to process. Your passport must be ready before you apply for the visa. The visa is not the same as the passport." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Waco Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WacoHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a Waco-area acceptance facility. We provide the nearest location with available appointments." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed application via overnight courier. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit through our registered State Department courier channel. You receive tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Your passport is delivered to your Waco address via secure overnight shipping within your chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Waco Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WacoFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Waco Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WacoResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Waco Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { href: "https://travel.state.gov/content/travel/en/passports.html", icon: "ri-government-line", title: "U.S. State Department", desc: "Official passport information, forms, and processing status updates." },
            { href: "https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html", icon: "ri-earth-line", title: "Travel Advisories", desc: "Current safety and entry requirements for every country." },
            { href: "https://www.cbp.gov/travel/travel-industry-persons/visa-waiver-program", icon: "ri-visa-line", title: "Visa Waiver Program", desc: "Check if your destination participates in the ESTA visa waiver program." },
          ].map((r, i) => (
            <a key={i} href={r.href} target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${r.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{r.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{r.desc}</p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function WacoFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Waco Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">Whether you are a Baylor student with a study abroad deadline, a church volunteer preparing for a mission trip, or a Waco family planning a vacation, we will get your passport processed on time.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function WacoPage() {
  useEffect(() => {
    document.title = "Emergency Passport Waco TX — Expedited Service for Baylor & Central Texas | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/waco/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Waco TX for Baylor students, mission groups, and Central Texas families. As fast as 24 hours. A+ BBB rated registered State Dept. courier. Best price guarantee.");
    setMeta("geo.placename", "Waco, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "31.5493;-97.1467");
    setMeta("ICBM", "31.5493, -97.1467");
    const graph = [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        { "@type": "ListItem", position: 2, name: "Texas", item: "https://fastpassportcenter.com/texas/" },
        { "@type": "ListItem", position: 3, name: "Waco Passport Services", item: pageUrl },
      ]},
      { "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data-waco";
    document.getElementById("structured-data-waco")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    return () => { document.getElementById("structured-data-waco")?.remove(); };
  }, []);

  return (
    <div className="font-sans">
      <WacoHeroSection />
      <WacoStickyNav />
      <WacoProcessingSection />
      <WacoServicesSection />
      <WacoLocalAreaSection />
      <WacoTravelTimingSection />
      <WacoDestinationsSection />
      <WacoTestimonialsSection />
      <WacoFacilitiesSection />
      <WacoComparisonSection />
      <WacoMistakesSection />
      <WacoHowItWorksSection />
      <WacoFAQSection />
      <WacoResourcesSection />
      <SEOCrossLinks currentRoute="/cities/waco" state="TX" />
      <WacoFinalCTA />
    </div>
  );
}